/**
 * Competition Mapping page — Slice 3 (Feb 2026).
 *
 * Shared org-wide market-intel log: any authenticated user can create a
 * mapping (competitor product vs one of our products, price delta, notes,
 * optional attachment). Only the creator or NSM+ can edit / delete. Powers
 * the `auto:competition_mapping_count` goal metric — attribution is by
 * `mapped_by`, so each user's Q1 count is anchored to `mapped_at`.
 *
 * Kept as a flat table + modal (no side panel) since the data is naturally
 * shallow — a row is competitor + product + short notes.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, MagnifyingGlass, Trash, PencilSimple, Paperclip, ArrowUpRight,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';

import { confirm } from '../components/ConfirmDialog';
import { notify } from '../lib/notify';
import { InlineSkeleton } from '../components/Skeletons';
import { ProductPicker } from '../components/ProductPicker';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function CompetitionMapping() {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);

  const [error, setError] = useState('');
  const canManage = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/competition-mappings`);
      setItems(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load competition mappings');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/products`).then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  const filtered = items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (it.competitor_name || '').toLowerCase().includes(q) ||
      (it.competitor_product || '').toLowerCase().includes(q) ||
      (it.our_product_name || '').toLowerCase().includes(q) ||
      (it.mapped_by_name || '').toLowerCase().includes(q)
    );
  });

  const del = async (id) => {
    if (!await confirm({ message: 'Delete this competition mapping?' })) return;
    try {
      await axios.delete(`${API}/competition-mappings/${id}`);
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6" data-testid="competition-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Competition Mapping</h1>
          <p className="text-sm text-[#6D8E87] mt-1">Log competitor products spotted in the field. Shared org-wide market intel — feeds the Competition Mapping goal metric.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg hover:bg-[#008f86] transition"
          data-testid="create-cm-btn"
        >
          <Plus size={18} weight="bold" /> Log Competition
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search competitor, product, or logger"
            className="w-full pl-9 pr-4 py-2 border border-[#D5E8E4] rounded-lg text-sm"
            data-testid="cm-search"
          />
        </div>
        <span className="text-xs text-[#6D8E87]" data-testid="cm-count">{filtered.length} of {items.length} mappings</span>
      </div>

      {loading ? (
        <InlineSkeleton />
      ) : error ? (
        <div className="medical-card p-6 border border-rose-200 bg-rose-50" data-testid="cm-load-error">
          <p className="text-sm text-rose-700">{error}</p>
          <button onClick={load} className="mt-2 text-xs text-rose-800 underline">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="medical-card p-12 text-center" data-testid="cm-empty">
          <p className="text-[#3B5A54] font-medium">No competition mappings yet.</p>
          <p className="text-sm text-[#6D8E87] mt-1">Log competitor products, price differences, and market signals your team encounters.</p>
        </div>
      ) : (
        <div className="medical-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F9F7] text-left text-[11px] uppercase tracking-wide text-[#3B5A54]">
              <tr>
                <th className="px-4 py-3">Competitor</th>
                <th className="px-4 py-3">Competitor Product</th>
                <th className="px-4 py-3">Our Product</th>
                <th className="px-4 py-3">Price Delta</th>
                <th className="px-4 py-3">Logged By</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => {
                const isOwner = it.mapped_by === user?.sub || it.mapped_by === user?.id;
                const canEdit = isOwner || canManage;
                return (
                  <tr key={it.id} className="border-t border-[#F0F5F3] hover:bg-[#FAFCFB]" data-testid={`cm-row-${it.id}`}>
                    <td className="px-4 py-3 font-medium text-[#052A23]">{it.competitor_name}</td>
                    <td className="px-4 py-3">{it.competitor_product}</td>
                    <td className="px-4 py-3 text-[#3B5A54]">{it.our_product_name || <span className="text-[#98B4AE] italic">—</span>}</td>
                    <td className={`px-4 py-3 font-mono ${it.price_delta > 0 ? 'text-rose-600' : it.price_delta < 0 ? 'text-emerald-600' : 'text-[#6D8E87]'}`}>
                      {it.price_delta ? (it.price_delta > 0 ? '+' : '') + money(it.price_delta) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6D8E87]">{it.mapped_by_name} <span className="text-[#98B4AE]">({it.mapped_by_role})</span></td>
                    <td className="px-4 py-3 text-xs text-[#6D8E87]">{new Date(it.mapped_at).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {it.attachment_url && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${it.attachment_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#00a69c] hover:text-[#008f86]"
                            title="View attachment"
                            data-testid={`cm-attach-${it.id}`}
                          >
                            <Paperclip size={16} />
                          </a>
                        )}
                        {canEdit && (
                          <>
                            <button onClick={() => { setEditing(it); setShowModal(true); }} className="text-[#3B5A54] hover:text-[#052A23]" title="Edit" data-testid={`cm-edit-${it.id}`}>
                              <PencilSimple size={16} />
                            </button>
                            <button onClick={() => del(it.id)} className="text-rose-500 hover:text-rose-700" title="Delete" data-testid={`cm-delete-${it.id}`}>
                              <Trash size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CMModal
          initial={editing}
          products={products}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={async () => { setShowModal(false); setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

const CMModal = ({ initial, products, onClose, onSaved }) => {
  const [form, setForm] = useState({
    competitor_name: initial?.competitor_name || '',
    competitor_product: initial?.competitor_product || '',
    our_product_id: initial?.our_product_id || '',
    our_product_name: initial?.our_product_name || '',
    price_delta: initial?.price_delta || 0,
    notes: initial?.notes || '',
    attachment_url: initial?.attachment_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setErr('File too large (max 10 MB)');
      return;
    }
    setUploading(true); setErr('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/competition-mappings/upload-attachment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, attachment_url: r.data.file_url }));
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    setErr(''); setSaving(true);
    try {
      const payload = { ...form, price_delta: parseFloat(form.price_delta) || 0 };
      if (initial?.id) {
        await axios.put(`${API}/competition-mappings/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/competition-mappings`, payload);
      }
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'Log'} Competition Mapping</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Competitor Name *</label>
              <input value={form.competitor_name} onChange={e => setForm({ ...form, competitor_name: e.target.value })}
                placeholder="e.g. Meditek" className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="cm-competitor-name" />
            </div>
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Competitor Product *</label>
              <input value={form.competitor_product} onChange={e => setForm({ ...form, competitor_product: e.target.value })}
                placeholder="e.g. ICU Bed Model X-500" className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="cm-competitor-product" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Our Product (compared against)</label>
            <ProductPicker
              products={products}
              value={form.our_product_id}
              onChange={(id) => {
                const p = products.find((x) => x.id === id);
                setForm({ ...form, our_product_id: id, our_product_name: p?.name || '' });
              }}
              noneLabel="— No specific product / general —"
              showPrice={false}
              testid="cm-our-product"
            />
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Price Delta (+ = we&apos;re higher, − = we&apos;re lower)</label>
            <input type="number" step="any" value={form.price_delta} onChange={e => setForm({ ...form, price_delta: e.target.value })}
              placeholder="0" className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="cm-price-delta" />
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3} placeholder="Field observations, customer feedback, feature comparison…"
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="cm-notes" />
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Attachment (brochure / spec sheet / photo — max 10 MB)</label>
            <input type="file" onChange={e => uploadFile(e.target.files?.[0])} accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx"
              className="w-full text-xs" data-testid="cm-file-upload" />
            {uploading && <p className="text-xs text-[#00a69c] mt-1">Uploading…</p>}
            {form.attachment_url && !uploading && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${form.attachment_url}`} target="_blank" rel="noreferrer"
                className="text-xs text-[#00a69c] hover:underline mt-1 inline-flex items-center gap-1" data-testid="cm-attachment-link">
                <ArrowUpRight size={12} /> View uploaded file
              </a>
            )}
          </div>
          {err && <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700" data-testid="cm-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2 border border-[#D5E8E4] rounded-lg text-sm">Cancel</button>
            <button onClick={submit} disabled={saving || uploading || !form.competitor_name || !form.competitor_product}
              className="flex-1 py-2 bg-[#00a69c] text-white rounded-lg text-sm disabled:opacity-50" data-testid="cm-save-btn">
              {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Log Mapping')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
